/* assets/serial-lookup.js
   Unified serial lookup for homepage section (Hybrid D)
   - Supports: Fender, Gibson, Martin (basic/full heuristics)
   - Targets DOM IDs used by the section.
   Author: Garrett Park Guitars (namespaced serialLookup)
*/

(function serialLookupModule(){
  'use strict';

  /* ---------- Helpers ---------- */
  function el(id){ return document.getElementById(id); }
  function safe(s){ return (s==null)?'':String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
  function normalize(sn){ return (sn||'').toString().toUpperCase().replace(/\s+/g,'').trim(); }
  function digitsOnly(sn){ return (sn||'').toString().replace(/[^0-9]/g,''); }
  function twoDigitYearToFull(n){
    n = Number(n);
    if (isNaN(n)) return null;
    return (n <= 30) ? 2000 + n : 1900 + n;
  }

  /* ---------- Small brand-specific mini-engines ----------
     These are lighter versions of your page engines and intentionally
     self-contained so the homepage widget never depends on page assets.
  */

  // --- Martin (straightforward year->lastSerial mapping) ---
  const martinYearLast = (function(){
    const arr = [
      {year:1898, last:8348},{year:1899, last:8716},{year:1900, last:9128},{year:1901, last:9310},
      {year:1902, last:9528},{year:1903, last:9810},{year:1904, last:9988},{year:1905, last:10120},
      {year:1906, last:10329},{year:1907, last:10727},{year:1908, last:10883},{year:1909, last:11018},
      {year:1910, last:11203},{year:1911, last:11413},{year:1912, last:11565},{year:1913, last:11821},
      {year:1914, last:12047},{year:1915, last:12209},{year:1916, last:12390},{year:1917, last:12988},
      {year:1918, last:13450},{year:1919, last:14512},{year:1920, last:15848},
      {year:1921, last:16758},{year:1922, last:17839},{year:1923, last:19891},{year:1924, last:22008},
      {year:1925, last:24116},{year:1926, last:28689},{year:1927, last:34435},{year:1928, last:37568},
      {year:1929, last:40843},{year:1930, last:45317},{year:1931, last:49589},{year:1932, last:52590},
      {year:1933, last:55084},{year:1934, last:58679},{year:1935, last:61947},{year:1936, last:65176},
      {year:1937, last:68865},{year:1938, last:71866},{year:1939, last:74061},{year:1940, last:76734},
      {year:1941, last:80013},{year:1942, last:83107},{year:1943, last:86724},{year:1944, last:90149},
      {year:1945, last:93623},{year:1946, last:98158},{year:1947, last:103468},{year:1948, last:108269},
      {year:1949, last:112961},{year:1950, last:117961},{year:1951, last:122799},{year:1952, last:128436},
      {year:1953, last:134501},{year:1954, last:141345},{year:1955, last:147328},{year:1956, last:153225},
      {year:1957, last:159061},{year:1958, last:165576},{year:1959, last:171047},{year:1960, last:175689},
      {year:1961, last:181297},{year:1962, last:187384},{year:1963, last:193327},{year:1964, last:199626},
      {year:1965, last:207030},{year:1966, last:217215},{year:1967, last:230095},{year:1968, last:241925},
      {year:1969, last:256003},{year:1970, last:271633},{year:1971, last:294270},{year:1972, last:313302},
      {year:1973, last:333873},{year:1974, last:353387},{year:1975, last:371828},{year:1976, last:388800},
      {year:1977, last:399625},{year:1978, last:407800},{year:1979, last:419900},{year:1980, last:430300}
    ];
    return arr;
  })();

  function lookupMartin(serial){
    const n = parseInt(digitsOnly(serial),10);
    if(isNaN(n)) return {candidates:[{label:'Invalid Martin serial', confidence:'low', why:'No numeric serial provided.'}]};
    // after last known
    if(n > martinYearLast[martinYearLast.length-1].last) {
      return {candidates:[{label:'Likely post-1980', confidence:'low', why:'Serial exceeds the 1980 last-serial table.'}]};
    }
    for(let i=0;i<martinYearLast.length;i++){
      if(n <= martinYearLast[i].last){
        return {candidates:[{label:`Approx year: ${martinYearLast[i].year}`, confidence:'high', why:`Serial ${n} ≤ ${martinYearLast[i].last} (published table).`} ]};
      }
    }
    return {candidates:[{label:'Unknown', confidence:'low', why:'Not found in mapping.'}]};
  }

  // --- Fender miniature engine (condensed from your page engine) ---
  const fenderRanges = [
    {min:1, max:7000, year:1954},{min:7000, max:9000, year:1955},{min:9000, max:17000, year:1956},
    {min:17000, max:25000, year:1957},{min:25000, max:34000, year:1958},{min:34000, max:44000, year:1959},
    {min:44000, max:59000, year:1960},{min:59000, max:71000, year:1961},{min:71000, max:93000, year:1962},
    {min:93000, max:99999, year:1963}
  ];
  const fenderSix = [
    {min:100000,max:110000,note:'late 1965'},{min:110000,max:200000,note:'1966'},{min:200000,max:210000,note:'1967'},
    {min:210000,max:250000,note:'1968'},{min:250000,max:280000,note:'1969'},{min:280000,max:300000,note:'1970'},
    {min:300000,max:340000,note:'1971'},{min:340000,max:370000,note:'1972'},{min:370000,max:520000,note:'1973'}
  ];

  function lookupFender(sn){
    const s = normalize(sn);
    if(!s) return {candidates:[{label:'No serial provided', confidence:'low', why:'Enter a numeric or prefixed serial (e.g. L12345).'}]};
    // L-series
    if(/^L\d{5}$/.test(s)){
      const num = parseInt(s.slice(1),10);
      if(num <= 20000) return {candidates:[{label:'1963 (L-series)', confidence:'high', why:'L00001–L20000 → 1963.'}]};
      if(num <= 59000) return {candidates:[{label:'1964 (L-series)', confidence:'high', why:'L20001–L59000 → 1964.'}]};
      return {candidates:[{label:'1965 (L-series)', confidence:'high', why:'L59001+ → 1965.'}]};
    }
    // pure numeric 3-5
    if(/^\d{3,5}$/.test(s)){
      const n = parseInt(s,10);
      for(let r of fenderRanges){
        if(n >= r.min && n <= r.max) return {candidates:[{label:`Approx year: ${r.year}`, confidence:'medium', why:`Serial ${n} within ${r.min}–${r.max}.`} ]};
      }
      return {candidates:[{label:'Early 1950s (model dependent)', confidence:'low', why:'Short numeric neck-plate serials vary by model.'}]};
    }
    // six-digit
    if(/^\d{6}$/.test(s)){
      const n = parseInt(s,10);
      for(let r of fenderSix){
        if(n >= r.min && n <= r.max) return {candidates:[{label:r.note, confidence:'medium', why:`Serial ${n} within ${r.min}–${r.max}.`}]};
      }
      return {candidates:[{label:'1970s-era six-digit serial (approx)', confidence:'low', why:'6-digit blocks were used in Norlin years.'}]};
    }
    // prefix patterns (L handled earlier). Generic fallback
    const pref = s.match(/^([A-Z]{1,3})(\d{1,6})$/);
    if(pref){
      const prefix = pref[1];
      if(/^(MIJ|CIJ|JD|J)$/.test(prefix)) return {candidates:[{label:`Japan-made (${prefix})`, confidence:'medium', why:'Japan systems vary; provide photos for precision.'}]};
      if(/^L$/.test(prefix)) return {candidates:[{label:'L-series (see details)', confidence:'medium', why:'L-prefixes run 1963–1965.'}]};
      return {candidates:[{label:`Prefix ${prefix}`, confidence:'low', why:'Letter prefixes usually indicate model/factory-specific systems.'}]};
    }

    return {candidates:[{label:'Unknown Fender format', confidence:'low', why:'No quick match — try removing punctuation or use the Fender page for deeper checks.'}]};
  }

  // --- Gibson miniature engine (supports YXXXX bursts + 8-digit YDDDYRRR-ish) ---
  function lookupGibson(sn, madeInUSA){
    const s = normalize(sn);
    if(!s) return {candidates:[{label:'No serial provided', confidence:'low', why:'Enter a Gibson serial (e.g. 9 1234 or 70602542).'}]};

    // Y XXXX scheme (e.g., 9 1234 -> 1959; 'YXXXX' where leading 1-digit year + 4 digits)
    // Accept patterns like "9 1234", "91234"
    const yxxxx = s.match(/^([0-9])\s?([0-9]{4})$/);
    if(yxxxx){
      const y = parseInt(yxxxx[1],10);
      const probable = 1900 + y + (y >= 0 && y <= 9 ?  ( (y===0)? 1960 :  ( (y<=9)? 1950 + y : 1900 + y) ) : 1900+y);
      // Heuristic: 1950s bursts used leading digit as year (9 → 1959; 6 → 1956 etc.). We'll present concise explanation.
      const yearLabel = (y===9)? '1959' : `195${y}`;
      return {candidates:[{label:`Likely ${yearLabel} (Y XXXX pattern)`, confidence:'high', why:'Single leading year-digit + 4-digit block used on many 1950s Gibson solidbodies.'}]};
    }

    // 8-digit YDDDYPPP style (e.g., 70602542) -> try parse YYDDDNNN and YDDDYRRR heuristics
    if(/^[0-9]{8}$/.test(s)){
      // Try YYDDDNNN: first two digits YY
      const yy = parseInt(s.slice(0,2),10);
      const ddd = parseInt(s.slice(2,5),10);
      if(!isNaN(yy) && !isNaN(ddd) && ddd >=1 && ddd <= 366){
        const fullYear = twoDigitYearToFull(yy);
        if(fullYear) {
          const monthDay = `day ${String(ddd).padStart(3,'0')} of ${fullYear}`;
          return {candidates:[{label:`${fullYear} — ${monthDay}`, confidence:'medium', why:'8-digit YYDDDNNN style used by Gibson 1977 onward (when applicable).'}]};
        }
      }
      // Try YDDDYRRR: single leading year digit as decade cue
      const y1 = parseInt(s.charAt(0),10);
      const ddd2 = parseInt(s.slice(1,4),10);
      if(!isNaN(y1) && !isNaN(ddd2) && ddd2 >=1 && ddd2 <=366){
        // candidate decades 1977-1989 etc — choose likely 1977-1979 if madeInUSA true
        const candidates = [];
        const now = new Date();
        const currentYear = now.getFullYear();
        for(let y=1977; y<=currentYear; y++){
          if(y % 10 === y1) candidates.push(y);
        }
        const bestYear = (madeInUSA && candidates.length)? (candidates.find(y=>y>=1970 && y<1980) || candidates[candidates.length-1]) : (candidates[candidates.length-1] || candidates[0]);
        if(bestYear){
          return {candidates:[{label:`${bestYear} (day ${String(ddd2).padStart(3,'0')})`, confidence:'medium', why:'Parsed as YDDDYRRR-like pattern; Made in USA checkbox may increase confidence for 1970s.'}]};
        }
      }
      return {candidates:[{label:'8-digit Gibson style (ambiguous)', confidence:'low', why:'Could be YYDDDNNN or Gibson YDDDYRRR — check Made in USA stamp and pot codes for precision.'}]};
    }

    // 6-digit numeric heuristic for 1960s–70s
    if(/^[0-9]{6}$/.test(s)){
      const n = parseInt(s,10);
      if(n >= 100000 && n < 400000) return {candidates:[{label:'1960s – Norlin-era (approx)', confidence:'medium', why:'6-digit blocks used in 1960s; ranges reused.'}]};
      if(n >= 400000 && n < 800000) return {candidates:[{label:'Early 1970s possible (check Made in USA)', confidence:'low', why:'6-digit blocks persisted; Made in USA increases chance of 1970s.'}]};
      return {candidates:[{label:'Numeric Gibson serial (ambiguous)', confidence:'low', why:'Provide photos or use full Gibson guide for deeper analysis.'}]};
    }

    // fallback
    return {candidates:[{label:'Unknown Gibson format', confidence:'low', why:'No quick heuristic matched. Try the Gibson full guide.'}]};
  }

  /* ---------- Result rendering ---------- */
  function buildResultHTML(candidates){
    if(!Array.isArray(candidates) || candidates.length===0) return '<div><strong>No result</strong></div>';
    const best = candidates[0];
    let html = '<div class="serial-compact">';
    html += `<div><div class="serial-best">${safe(best.label)}</div><div class="serial-more">${safe(best.why || '')}</div></div>`;
    html += `<div><span class="serial-badge">${safe(best.confidence)}</span></div>`;
    html += '</div>';
    if(candidates.length > 1){
      html += '<div style="margin-top:10px;">Other possibilities:<ul style="margin:6px 0 0 18px;">';
      candidates.slice(1,5).forEach(c=>{ html += `<li>${safe(c.label)} — ${safe(c.why||'')}</li>`; });
      html += '</ul></div>';
    }
    html += '<div style="margin-top:12px;" class="serial-small">Tip: confirm with photos (headstock, pots, inside-body stamps) for the most reliable dating.</div>';
    return html;
  }

  /* ---------- Event wiring ---------- */
  function init(){
    const brandSel = el('serial-brand-select');
    const input = el('serial-unified-input');
    const btn = el('serial-unified-btn');
    const madeInUSA = el('serial-madeinusa');
    const out = el('serial-unified-result');

    if(!brandSel || !input || !btn || !out) return console.warn('serialLookup: UI elements not found.');

    function run(){
      const brand = (brandSel.value || '').toLowerCase();
      const raw = input.value || '';
      let res = {candidates:[{label:'No input', confidence:'low', why:'Enter a serial before searching.'}]};

      if(!raw || !raw.trim()){
        out.style.display = 'block';
        out.innerHTML = '<div style="color:#b00"><strong>Please enter a serial to look up.</strong></div>';
        return;
      }

      try{
        if(brand === 'martin') res = lookupMartin(raw);
        else if(brand === 'fender') res = lookupFender(raw);
        else if(brand === 'gibson') res = lookupGibson(raw, !!(madeInUSA && madeInUSA.checked));
        else res = {candidates:[{label:'Unsupported brand', confidence:'low', why:'Please select a supported brand.'}]};
      } catch(e){
        console.error('serialLookup error', e);
        res = {candidates:[{label:'Error', confidence:'low', why:'An unexpected error occurred.'}]};
      }

      out.style.display = 'block';
      out.innerHTML = buildResultHTML(res.candidates);
    }

    // Enter key support
    input.addEventListener('keydown', function(ev){ if(ev.key === 'Enter'){ ev.preventDefault(); run(); }});
    btn.addEventListener('click', run);
  }

  // Init on DOM ready
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Expose for debugging (safe global)
  window.__gpg_serial_lookup = {
    lookupMartin: lookupMartin,
    lookupFender: lookupFender,
    lookupGibson: lookupGibson
  };

})(); // end module
